import { ExperimentType } from '@shared/types/experiments'
import { useIsUserC360, useUser } from '@/features/account/services/User/hooks'
import { useSelectedUserGroup } from '@/features/account/services/Business/hooks'
import { GROUP_ID_PERSONAL } from '@/features/business/utils/selectedUserGroupCookie'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { useFeatureFlags } from '@/features/shared/services/FeatureFlags/useFeatureFlags'
import { type UserGroup } from '@/features/account/services/User/types'

export type PersonaState = {
  /** All user groups associated with the account, or undefined while loading */
  groups: UserGroup[] | undefined
  /** The currently selected business group, or null if personal context is active */
  activeGroup: UserGroup | null
  /** True when the business profile phase 1 feature flag is enabled, the user has
   * at least one group, and the user is not excluded for being on a Circle 360 plan */
  isFeatureEnabled: boolean
  /** True when the user is eligible for business and either the business creation
   * or the business nudge experiment is enabled */
  isBusinessCreationEnabled: boolean
  /** True when either the business creation or the business nudge experiment is
   * active. This reflects experiment membership only — it says nothing about
   * whether the user is otherwise eligible to create a business profile. */
  isInBusinessExperiment: boolean
  /** True when the user is eligible for business and the business nudge
   * experiment is enabled */
  shouldShowBusinessNudge: boolean
  /** True when a business group is currently selected (activeGroup is non-null) */
  isBusinessActive: boolean
  /** True when a business group is active and the current user is not its owner */
  isBusinessNonOwner: boolean
  /** True when the user belongs to at least one business group */
  isBusinessUser: boolean
  /** True when the user has explicitly chosen a persona (group or personal) */
  personaSelected: boolean
  /** Alias for activeGroup — the active business group, or null if in personal context */
  business: UserGroup | null
  /** True when the current user is the owner of the active business group */
  isOwner: boolean
  /** Sets the active group by group ID; pass GROUP_ID_PERSONAL to switch to personal context */
  setActiveGroup: (groupId: string) => void
}

export const usePersona = (): PersonaState => {
  const groups = useUser()?.groups
  const { selectedUserGroupId, setSelectedUserGroupId } = useSelectedUserGroup()
  const {
    flags: { business_profile_phase_1, business_profile_c360 },
  } = useFeatureFlags()
  const { active: isBusinessCreationExpFlagEnabled } = useExperiment(
    ExperimentType.WEB_BUSINESS_CREATE_ENABLED,
  )
  const { active: isBusinessNudgesExpFlagEnabled } = useExperiment(
    ExperimentType.WEB_BUSINESS_NUDGES,
  )
  const isUserC360 = useIsUserC360()

  const activeGroup =
    selectedUserGroupId && selectedUserGroupId !== GROUP_ID_PERSONAL
      ? (groups?.find((group) => group.id === selectedUserGroupId) ?? null)
      : null

  const personaSelected = !!selectedUserGroupId

  const userId = useUser()?.id
  const isBusinessUser = (groups?.length ?? 0) > 0
  // Circle 360 members are only excluded while business_profile_c360 is off
  const isExcludedC360User = isUserC360 && !business_profile_c360
  const isFeatureEnabled =
    Boolean(business_profile_phase_1) && isBusinessUser && !isExcludedC360User
  const isEligibleForBusiness =
    Boolean(business_profile_phase_1) && !isBusinessUser && !isExcludedC360User
  // Nudged users must be able to create a business even if they're outside a launch metro
  const isInBusinessExperiment = isBusinessCreationExpFlagEnabled || isBusinessNudgesExpFlagEnabled
  const isBusinessCreationEnabled = isEligibleForBusiness && isInBusinessExperiment
  const shouldShowBusinessNudge = isEligibleForBusiness && isBusinessNudgesExpFlagEnabled
  const isBusinessActive = activeGroup !== null
  const isOwner =
    activeGroup !== null && userId !== undefined && String(userId) === activeGroup.ownerId
  const isBusinessNonOwner = isBusinessActive && !isOwner

  return {
    groups,
    activeGroup,
    isFeatureEnabled,
    isBusinessCreationEnabled,
    isInBusinessExperiment,
    shouldShowBusinessNudge,
    isBusinessActive,
    isBusinessNonOwner,
    isBusinessUser,
    personaSelected,
    business: activeGroup,
    isOwner,
    setActiveGroup: setSelectedUserGroupId,
  }
}
